export type FindationBrand = {
	id: number
	name: string
	search: string
}

export type FindationProduct = {
	id: number
	brandId: number
	name: string
	search: string
}

export type FindationShade = {
	id: number
	productId: number
	name: string
	search: string
}

export type FindationSearchResult =
	| {
			kind: 'match'
			externalId: string | null
			productName: string
			recommendedShade: string
	  }
	| { kind: 'more-data' }

export type FindationErrorCode =
	| 'aborted'
	| 'http'
	| 'malformed'
	| 'network'
	| 'timeout'

export class FindationRequestError extends Error {
	constructor(public readonly code: FindationErrorCode) {
		super(`Findation request failed: ${code}`)
		this.name = 'FindationRequestError'
	}
}

type FetchImplementation = (
	input: RequestInfo | URL,
	init?: RequestInit
) => Promise<Response>

const API_BASE_URL = 'https://findation.com'
const DEFAULT_TIMEOUT_MS = 10_000

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null
}

function requiredNumber(value: unknown): number | undefined {
	if (typeof value === 'number' && Number.isFinite(value)) return value
	if (typeof value !== 'string' || value.trim() === '') return undefined
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : undefined
}

function requiredString(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim() !== ''
		? value.trim()
		: undefined
}

function optionalIdentifier(value: unknown): string | undefined {
	if (typeof value === 'number' && Number.isSafeInteger(value)) return `${value}`
	return requiredString(value)
}

function searchableString(value: unknown, fallback: string): string {
	return typeof value === 'string' ? value : fallback
}

export class FindationClient {
	constructor(
		private readonly apiKey: string,
		private readonly fetchImplementation: FetchImplementation = globalThis.fetch.bind(globalThis),
		private readonly timeoutMs = DEFAULT_TIMEOUT_MS
	) {}

	async isAvailable(productUrl: string, signal?: AbortSignal): Promise<boolean> {
		const value = await this.request('/api/v2/should_show_findation', {
			method: 'POST',
			body: JSON.stringify({ product_url: productUrl }),
		}, signal)
		if (!isRecord(value) || typeof value.available !== 'boolean') {
			throw new FindationRequestError('malformed')
		}
		return value.available
	}

	async getBrands(signal?: AbortSignal): Promise<FindationBrand[]> {
		const value = await this.request('/api/v2/brands', {}, signal)
		if (!Array.isArray(value)) throw new FindationRequestError('malformed')
		return value.map((item) => {
			if (!isRecord(item)) throw new FindationRequestError('malformed')
			const id = requiredNumber(item.id)
			const name = requiredString(item.name)
			if (id === undefined || !name) throw new FindationRequestError('malformed')
			return { id, name, search: searchableString(item.search, name) }
		})
	}

	async getProducts(brandId: number, signal?: AbortSignal): Promise<FindationProduct[]> {
		const params = new URLSearchParams({ brand_id: `${brandId}` })
		const value = await this.request(`/api/v2/products?${params}`, {}, signal)
		if (!Array.isArray(value)) throw new FindationRequestError('malformed')
		return value.map((item) => {
			if (!isRecord(item)) throw new FindationRequestError('malformed')
			const id = requiredNumber(item.id)
			const itemBrandId = requiredNumber(item.brand_id)
			const name = requiredString(item.name)
			if (id === undefined || itemBrandId === undefined || !name) {
				throw new FindationRequestError('malformed')
			}
			return {
				id,
				brandId: itemBrandId,
				name,
				search: searchableString(item.search, name),
			}
		})
	}

	async getShades(productId: number, signal?: AbortSignal): Promise<FindationShade[]> {
		const params = new URLSearchParams({ product_id: `${productId}` })
		const value = await this.request(`/api/v2/shades?${params}`, {}, signal)
		if (!Array.isArray(value)) throw new FindationRequestError('malformed')
		return value.map((item) => {
			if (!isRecord(item)) throw new FindationRequestError('malformed')
			const id = requiredNumber(item.id)
			const itemProductId = requiredNumber(item.product_id)
			const name = requiredString(item.name)
			if (id === undefined || itemProductId === undefined || !name) {
				throw new FindationRequestError('malformed')
			}
			return {
				id,
				productId: itemProductId,
				name,
				search: searchableString(item.search, name),
			}
		})
	}

	async search(
		shadeIds: number[],
		productUrl: string,
		signal?: AbortSignal
	): Promise<FindationSearchResult> {
		const value = await this.request('/api/v2/search', {
			method: 'POST',
			body: JSON.stringify({ shade_ids: shadeIds, product_url: productUrl }),
		}, signal)
		if (!isRecord(value)) throw new FindationRequestError('malformed')
		if (value.error_type === 'more_data_required') return { kind: 'more-data' }

		const recommendedShade = requiredString(value.recommended_shade)
		const productName = requiredString(value.product_name)
		if (!recommendedShade || !productName) {
			throw new FindationRequestError('malformed')
		}
		return {
			kind: 'match',
			externalId: optionalIdentifier(value.external_id) ?? null,
			productName,
			recommendedShade,
		}
	}

	private async request(
		path: string,
		init: RequestInit,
		signal?: AbortSignal
	): Promise<unknown> {
		const controller = new AbortController()
		let timedOut = false
		const abortFromCaller = () => controller.abort()
		signal?.addEventListener('abort', abortFromCaller, { once: true })
		const timeout = globalThis.setTimeout(() => {
			timedOut = true
			controller.abort()
		}, this.timeoutMs)

		try {
			const response = await this.fetchImplementation(`${API_BASE_URL}${path}`, {
				...init,
				headers: {
					Accept: 'application/json',
					Authorization: `Bearer ${this.apiKey}`,
					...(init.body ? { 'Content-Type': 'application/json' } : {}),
				},
				signal: controller.signal,
			})
			if (!response.ok) throw new FindationRequestError('http')
			return await response.json()
		} catch (error) {
			if (error instanceof FindationRequestError) throw error
			if (controller.signal.aborted) {
				throw new FindationRequestError(timedOut ? 'timeout' : 'aborted')
			}
			throw new FindationRequestError('network')
		} finally {
			globalThis.clearTimeout(timeout)
			signal?.removeEventListener('abort', abortFromCaller)
		}
	}
}
